// src/components/risk/RecommendedActionsCard.jsx
// AI-suggested actions based on client's risk factors

import React from 'react';
import {
  Phone,
  MessageSquare,
  Mail,
  Calendar,
  FileText,
  Heart,
  AlertTriangle,
  CheckCircle2,
  ArrowRight,
  Lightbulb
} from 'lucide-react';

// Map factors to recommended actions - prioritized by actual detected factors
const generateRecommendations = (factors = [], score = 0) => {
  const recommendations = [];

  // Sort factors by points (highest impact first)
  const sortedFactors = [...factors].sort((a, b) => Math.abs(b.points || 0) - Math.abs(a.points || 0));

  // Process top factors to generate specific recommendations
  for (const factor of sortedFactors) {
    const name = (factor.name || '').toLowerCase();
    const code = (factor.code || '').toLowerCase();

    // Skip if we already have 4 recommendations
    if (recommendations.length >= 4) break;

    // Skip baseline/unknown factors
    if (code === 'no_blue_button_baseline' || name.includes('no claims data') || name.includes('baseline')) {
      continue;
    }

    // Annual review overdue
    if (name.includes('annual review') || code.includes('annual_review')) {
      recommendations.push({
        icon: Calendar,
        title: 'Schedule Annual Review',
        description: 'Client is overdue for their annual plan review.',
        priority: 'high',
        action: 'annual_review',
        factorCode: factor.code
      });
      continue;
    }

    // Welcome call missed
    if (name.includes('welcome call') || code.includes('welcome_call') || name.includes('day 7')) {
      recommendations.push({
        icon: Phone,
        title: 'Complete Welcome Call',
        description: 'Welcome call was not completed. Reach out to introduce yourself.',
        priority: 'high',
        action: 'call',
        factorCode: factor.code
      });
      continue;
    }

    // No contact issues
    if (name.includes('no contact') || code.includes('no_contact') || name.includes('days since')) {
      recommendations.push({
        icon: Phone,
        title: 'Client Outreach Needed',
        description: `Client hasn't been contacted recently. Schedule a check-in call.`,
        priority: 'high',
        action: 'call',
        factorCode: factor.code
      });
      continue;
    }

    // SMS no response
    if (name.includes('sms') && name.includes('no response')) {
      recommendations.push({
        icon: Phone,
        title: 'Try Calling Instead',
        description: 'SMS attempts unsuccessful. Try a phone call instead.',
        priority: 'medium',
        action: 'call',
        factorCode: factor.code
      });
      continue;
    }

    // AEP/OEP
    if (name.includes('aep') || name.includes('oep') || name.includes('enrollment')) {
      recommendations.push({
        icon: Calendar,
        title: 'Enrollment Period Outreach',
        description: 'Enrollment period relevant. Review current plan and options.',
        priority: 'high',
        action: 'call',
        factorCode: factor.code
      });
      continue;
    }

    // Life events / SEP
    if (factor.category === 'life_events' || factor.category === 'life_event' || name.includes('sep')) {
      recommendations.push({
        icon: Calendar,
        title: 'Check SEP Eligibility',
        description: 'Life event may qualify client for Special Enrollment Period.',
        priority: 'high',
        action: 'call',
        factorCode: factor.code
      });
      continue;
    }

    // Real utilization concerns (not baseline)
    if (factor.category === 'utilization' && factor.points > 10) {
      recommendations.push({
        icon: Heart,
        title: 'Review Healthcare Utilization',
        description: 'High utilization detected. Verify plan meets their needs.',
        priority: 'medium',
        action: 'call',
        factorCode: factor.code
      });
      continue;
    }
  }

  // Default for high scores with no specific recommendations
  if (score >= 65 && recommendations.length === 0) {
    recommendations.push({
      icon: Phone,
      title: 'Immediate Outreach Required',
      description: 'High risk score detected. Personal contact recommended.',
      priority: 'high',
      action: 'call'
    });
  }

  // Default for moderate scores with no specific recommendations
  if (score >= 35 && score < 65 && recommendations.length === 0) {
    recommendations.push({
      icon: Phone,
      title: 'Check-in Call',
      description: 'Moderate risk - reach out to maintain relationship.',
      priority: 'medium',
      action: 'call'
    });
  }

  return recommendations;
};

const RecommendedActionsCard = ({
  factors = [],
  score = 0,
  onActionClick,
  className = ''
}) => {
  const recommendations = generateRecommendations(factors, score);

  if (recommendations.length === 0) {
    return (
      <div className={`bg-slate-800/50 rounded-xl border border-slate-700 p-4 ${className}`}>
        <div className="flex items-center gap-2 text-slate-400">
          <CheckCircle2 className="w-5 h-5 text-green-400" />
          <span>No urgent actions needed at this time</span>
        </div>
      </div>
    );
  }

  const priorityColors = {
    high: 'border-red-500/30 bg-red-900/10',
    medium: 'border-yellow-500/30 bg-yellow-900/10',
    low: 'border-green-500/30 bg-green-900/10'
  };

  const priorityLabels = {
    high: { text: 'High Priority', color: 'text-red-400' },
    medium: { text: 'Recommended', color: 'text-yellow-400' },
    low: { text: 'Optional', color: 'text-green-400' }
  };

  return (
    <div className={`bg-slate-800/50 rounded-xl border border-slate-700 ${className}`}>
      <div className="p-4 border-b border-slate-700">
        <div className="flex items-center gap-2">
          <Lightbulb className="w-5 h-5 text-amber-400" />
          <h3 className="text-lg font-semibold text-white">Recommended Actions</h3>
        </div>
      </div>

      <div className="p-4 space-y-3">
        {recommendations.map((rec, idx) => {
          const IconComponent = rec.icon;
          return (
            <button
              key={idx}
              onClick={() => onActionClick?.(rec.action, rec)}
              className={`w-full text-left p-3 rounded-lg border ${priorityColors[rec.priority]} hover:bg-slate-700/30 transition-colors`}
            >
              <div className="flex items-start gap-3">
                <div className="p-2 bg-slate-700/50 rounded-lg">
                  <IconComponent className="w-5 h-5 text-amber-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <h4 className="font-medium text-white">{rec.title}</h4>
                    <span className={`text-xs font-medium ${priorityLabels[rec.priority].color}`}>
                      {priorityLabels[rec.priority].text}
                    </span>
                  </div>
                  <p className="text-sm text-slate-400 mt-1">{rec.description}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-500 flex-shrink-0 mt-1" />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default RecommendedActionsCard;
